import { useState } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, Check, Copy, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { captureError } from '@/lib/logger';
import { signingLink } from '@/lib/agreements/constants';
import { isSigningLinkLive } from '@/lib/agreements/tokens';
import type { AgreementTokenRow, IssuedToken } from '@/lib/agreements/api';

/** The two parties that sign through a shared link, and how each is spoken about. */
const PARTY_COPY = {
    WITNESS: {
        title: 'Witness signing link',
        recipient: 'the witness',
        share: 'yourself — by message, or in person. Nothing is emailed.',
        issuing: 'Issuing one puts the document out to the witness and returns a URL you can copy once.',
    },
    DRIVER: {
        title: 'Depositor signing link',
        recipient: 'the depositor',
        share:
            'yourself — by message, or in person. They need no account and nothing is emailed; ' +
            'the link is the whole of their access.',
        issuing:
            'Issuing one puts the document out to the depositor and returns a URL you can copy once.',
    },
} as const;

interface SigningLinkDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** WITNESS on a contract document, DRIVER on the deposit arc's depositor link. */
    party: 'WITNESS' | 'DRIVER';
    /** Who the link is for, when the record knows. */
    recipientName: string | null;
    /** The token just minted, if any. The raw value exists only in this render. */
    issued: IssuedToken | null;
    /** The live link's stored half — everything about it except the token itself. */
    activeToken: AgreementTokenRow | null;
    isIssuing: boolean;
    isRevoking: boolean;
    onIssue: () => void;
    onRevoke: () => void;
}

/**
 * A signing link: minted here, copied here, shared by hand.
 *
 * THE RAW TOKEN EXISTS EXACTLY ONCE, in the response to `agreement_issue_token`. The
 * database stores only its sha256 and an eight-character prefix, so a link that is closed
 * without being copied cannot be recovered — it can only be replaced, which revokes the
 * one it replaces. The dialog therefore shows the full URL rather than a "Copy" button
 * alone: an admin has to be able to see what they are about to paste into a chat.
 *
 * ONE DIALOG FOR BOTH PARTIES, because the mechanism is identical — one token table, one
 * public route, one ceremony — and only the sentence naming the recipient differs. What is
 * genuinely different between them lives where it belongs: in the preconditions the callers
 * enforce before they offer this at all.
 */
export default function SigningLinkDialog({
    open,
    onOpenChange,
    party,
    recipientName,
    issued,
    activeToken,
    isIssuing,
    isRevoking,
    onIssue,
    onRevoke,
}: SigningLinkDialogProps) {
    const [copied, setCopied] = useState(false);
    const copy = PARTY_COPY[party];
    const link = issued ? signingLink(issued.token) : null;

    const handleCopy = async () => {
        if (!link) return;
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
            toast.success('Signing link copied');
        } catch (error) {
            // Clipboard access can be refused; the link is on screen and selectable. The
            // error itself is worth reporting — a browser that never copies is a browser
            // where every signing link has to be transcribed by hand.
            captureError(error, { tags: { area: 'signing-link-copy' } });
            toast.error('Could not copy automatically — select the link and copy it.');
        }
    };

    const live = isSigningLinkLive(activeToken);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{copy.title}</DialogTitle>
                    <DialogDescription>
                        Share this with <b>{recipientName || copy.recipient}</b> {copy.share}
                    </DialogDescription>
                </DialogHeader>

                {link ? (
                    <>
                        <div className="rounded-lg border bg-muted/40 p-3">
                            <p className="break-all font-mono text-xs">{link}</p>
                        </div>
                        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                            <span>
                                Copy it now. This is the only time the link is readable — closing this dialog
                                does not delete it, but it cannot be shown again. It expires{' '}
                                {format(new Date(issued!.expires_at), 'MMM d, yyyy · HH:mm')}.
                            </span>
                        </div>
                        <Button onClick={handleCopy} className="w-full">
                            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                            {copied ? 'Copied' : 'Copy signing link'}
                        </Button>
                    </>
                ) : (
                    <div className="space-y-3 text-sm">
                        {live ? (
                            <div className="rounded-lg border bg-muted/40 p-3">
                                <p>
                                    A link is already out, issued{' '}
                                    {format(new Date(activeToken!.created_at), 'MMM d, yyyy · HH:mm')} and
                                    expiring {format(new Date(activeToken!.expires_at), 'MMM d, yyyy · HH:mm')}.
                                </p>
                                <p className="mt-1 font-mono text-xs text-muted-foreground">
                                    starts {activeToken!.token_prefix}…
                                </p>
                                <p className="mt-2 text-muted-foreground">
                                    The link itself was only readable when it was created. Issue a new one to
                                    get a fresh copyable link — that revokes this one.
                                </p>
                            </div>
                        ) : (
                            <p className="text-muted-foreground">No live link. {copy.issuing}</p>
                        )}
                    </div>
                )}

                <DialogFooter className="sm:justify-between">
                    <div>
                        {live && (
                            <Button variant="ghost" onClick={onRevoke} disabled={isRevoking}>
                                {isRevoking ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Trash2 className="mr-2 h-4 w-4" />
                                )}
                                Revoke link
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            Close
                        </Button>
                        <Button onClick={onIssue} disabled={isIssuing}>
                            {isIssuing ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <RefreshCw className="mr-2 h-4 w-4" />
                            )}
                            {live || issued ? 'Issue a new link' : 'Issue signing link'}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
